import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Confirmation dialog for disqualifying a specific bidder from a live auction.
 *
 * This is a FINAL action:
 *  - the bidder's future bid attempts are rejected by the backend,
 *  - their existing bids are excluded from ranking / award selection,
 *  - the auction appears CLOSED for them with a red banner showing the reason,
 *  - the reason is emailed to the bidder verbatim.
 *
 * The remarks textarea is mandatory — we won't send a "no reason" email.
 */
export const DisqualifyDialog = ({ open, onOpenChange, auctionId, bidder, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const clean = reason.trim();
    if (!clean) {
      toast.error("Please add a reason. It will be included in the email.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/auctions/${auctionId}/disqualify`, {
        bidder_email: bidder.email,
        reason: clean,
      });
      toast.success(`Disqualified ${bidder.email}. Notification email sent.`);
      onOpenChange(false);
      setReason("");
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to disqualify");
    } finally {
      setBusy(false);
    }
  };

  const close = () => { if (!busy) onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="rounded-none max-w-lg" data-testid="disqualify-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tightest flex items-center gap-2">
            <Warning size={20} weight="fill" className="text-[#B91C1C]" />
            Disqualify bidder
          </DialogTitle>
        </DialogHeader>

        <div className="border-l-2 border-[#B91C1C] pl-3 text-sm text-neutral-700 leading-relaxed">
          You are about to disqualify{" "}
          <span className="font-semibold">{bidder?.name || bidder?.email}</span>
          {" "}from this auction. This is <strong>final</strong> — their bids will be
          excluded from ranking, they cannot submit new bids, and the auction will
          appear as closed for them with your reason shown as a red banner.
        </div>

        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold block mb-1.5">
            Reason (required — sent to the bidder by email)
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={5}
            placeholder="e.g. Vendor is under compliance review and cannot be considered for this RFQ."
            className="rounded-none border-black/20"
            disabled={busy}
            data-testid="disqualify-reason"
          />
          <p className="text-[11px] text-neutral-500 mt-1.5">
            This message will be included verbatim in the notification email.
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={close}
            disabled={busy}
            className="flex-1 rounded-none border-black h-11 btn-sharp"
            data-testid="disqualify-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy || !reason.trim()}
            className="flex-1 rounded-none bg-[#B91C1C] hover:bg-[#7F1D1D] text-white h-11 btn-sharp"
            data-testid="disqualify-confirm"
          >
            {busy ? "Disqualifying..." : "Disqualify & notify"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
